import { DEMOGRAPHIC_IDS, ReportDemographic } from "../types/report";
import { GetDemographicGroupsResponse } from "../types/workplaceCulture";

export const getOrgInclusionScores = (
  demographicsData?: GetDemographicGroupsResponse
): {
  overallScore: number | null;
  chapterScores: Record<string, number | null>;
} => {
  if (!demographicsData?.demographic_groups)
    return { overallScore: null, chapterScores: {} };

  const orgDemographic = findDemographicById(
    demographicsData,
    DEMOGRAPHIC_IDS.ORGANISATION
  );

  return {
    overallScore: orgDemographic?.score ?? null,
    // return chapterScore as key value pairs where the id is the key and the score is the value
    chapterScores: orgDemographic?.chapter_scores
      ? orgDemographic.chapter_scores.reduce((acc, cs) => {
          acc[cs.id] = cs.score ?? null;
          return acc;
        }, {} as Record<string, number | null>)
      : {},
  };
};

// Extracts all chapter IDs from the organisation demographic's chapter_scores
export const getChapterIdsFromDemographicsData = (
  demographicsData?: GetDemographicGroupsResponse
): string[] => {
  if (!demographicsData?.demographic_groups) return [];

  const orgDemographic = findDemographicById(
    demographicsData,
    DEMOGRAPHIC_IDS.ORGANISATION
  );

  return orgDemographic?.chapter_scores
    ? orgDemographic.chapter_scores.map((cs) => cs.id)
    : [];
};

/**
 * Helper function to find a specific demographic by ID.
 * Returns the full demographic object with all properties.
 */
export const findDemographicById = (
  demographicsData: GetDemographicGroupsResponse | undefined,
  demographicId: string
) => {
  if (!demographicsData?.demographic_groups) return undefined;

  for (const group of demographicsData.demographic_groups) {
    const demographic = group.demographics.find(
      (demo) => demo.id === demographicId
    );
    if (demographic) {
      return demographic;
    }
  }

  return undefined;
};

/**
 * Helper function to find multiple demographics by their IDs.
 * Returns a simplified array of objects for display purposes.
 * Transforms engagement_percent from decimal (0.44) to percentage (44).
 */
export const findDemographicsByIds = (
  demographicsData: GetDemographicGroupsResponse | undefined,
  targetIds: string[]
) => {
  if (!demographicsData?.demographic_groups) return [];

  const results: ReportDemographic[] = [];

  targetIds.forEach((id) => {
    const demographic = findDemographicById(demographicsData, id);
    if (demographic) {
      results.push({
        label: demographic.label,
        engagement: demographic.engagement || 0,
        engagement_percent: demographic.engagement_percent
          ? demographic.engagement_percent * 100
          : 0,
      });
    }
  });

  return results;
};
